import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  LinearProgress,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";

const STATUS_UI = {
  ok: {
    label: "Normal",
    color: "#16a34a",
    soft: "rgba(22, 163, 74, 0.12)",
    gradient: "linear-gradient(135deg, rgba(22,163,74,0.18), rgba(255,255,255,0.95))",
    icon: <CheckCircleRoundedIcon fontSize="small" />,
  },
  warn: {
    label: "Advertencia",
    color: "#d97706",
    soft: "rgba(217, 119, 6, 0.12)",
    gradient: "linear-gradient(135deg, rgba(217,119,6,0.18), rgba(255,255,255,0.95))",
    icon: <WarningAmberRoundedIcon fontSize="small" />,
  },
  alarm: {
    label: "Alarma",
    color: "#dc2626",
    soft: "rgba(220, 38, 38, 0.12)",
    gradient: "linear-gradient(135deg, rgba(220,38,38,0.18), rgba(255,255,255,0.95))",
    icon: <ErrorOutlineRoundedIcon fontSize="small" />,
  },
  nodata: {
    label: "Sin datos",
    color: "#64748b",
    soft: "rgba(100, 116, 139, 0.12)",
    gradient: "linear-gradient(135deg, rgba(100,116,139,0.14), rgba(255,255,255,0.95))",
    icon: <HelpOutlineRoundedIcon fontSize="small" />,
  },
};

function buildProgressValue(result) {
  if (
    result?.deltaT == null ||
    result?.expectedMin == null ||
    result?.expectedMax == null ||
    result?.alarmMin == null
  ) {
    // Algoritmos sin curva de deltaT (como falta de refrigerante):
    // usamos el estado directamente en vez de un diferencial.
    if (result?.status === "alarm") return 100;
    if (result?.status === "warn") return 50;
    return 0;
  }

  const deltaT = Number(result.deltaT);

  if (deltaT < result.expectedMin) return 100;
  if (deltaT >= result.alarmMin) return 100;

  if (deltaT >= result.expectedMin && deltaT <= result.expectedMax) {
    return 0;
  }

  const warningRange = result.alarmMin - result.expectedMax;
  if (warningRange <= 0) return 100;

  const raw = ((deltaT - result.expectedMax) / warningRange) * 100;
  return Math.max(0, Math.min(raw, 100));
}

function formatNumber(value, decimals = 1) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(decimals);
}

function LedIndicator({ status }) {
  const ui = STATUS_UI[status] || STATUS_UI.nodata;

  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        bgcolor: ui.color,
        boxShadow: `0 0 0 5px ${ui.soft}, 0 0 18px ${ui.color}`,
        flexShrink: 0,
      }}
    />
  );
}

function MetricPill({ label, value }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.1,
        borderRadius: 2,
        bgcolor: "rgba(248, 250, 252, 0.9)",
        border: "1px solid #e5e7eb",
        minWidth: 120,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Typography variant="body2" fontWeight={800} sx={{ mt: 0.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function ConditionPill({ label, value, alarm }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.1,
        borderRadius: 2,
        bgcolor: alarm ? "rgba(220, 38, 38, 0.08)" : "rgba(248, 250, 252, 0.9)",
        border: alarm ? "1px solid rgba(220, 38, 38, 0.4)" : "1px solid #e5e7eb",
        minWidth: 120,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={800}
        sx={{ mt: 0.2, color: alarm ? "#dc2626" : "inherit" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function AlgorithmCard({ result }) {
  const ui = STATUS_UI[result.status] || STATUS_UI.nodata;
  const progress = buildProgressValue(result);

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(226,232,240,0.9)",
        background: ui.gradient,
        boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -40,
          right: -40,
          width: 140,
          height: 140,
          borderRadius: "50%",
          bgcolor: ui.soft,
        }}
      />

      <CardContent sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LedIndicator status={result.status} />

            <Box>
              <Typography variant="subtitle1" fontWeight={900}>
                {result.title}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                {result.message}
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={ui.icon}
            label={ui.label}
            sx={{
              bgcolor: ui.soft,
              color: ui.color,
              fontWeight: 800,
              borderRadius: 999,
              "& .MuiChip-icon": {
                color: ui.color,
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {result.deltaT != null && (
            <>
              <MetricPill
                label="ΔT actual"
                value={`${formatNumber(result.deltaT)} °C`}
              />

              <MetricPill
                label="Correcto"
                value={
                  result.expectedMin != null && result.expectedMax != null
                    ? `${formatNumber(result.expectedMin)} a ${formatNumber(result.expectedMax)} °C`
                    : "—"
                }
              />

              <MetricPill
                label="Advertencia"
                value={
                  result.expectedMax != null && result.alarmMin != null
                    ? `> ${formatNumber(result.expectedMax)} a < ${formatNumber(result.alarmMin)} °C`
                    : "—"
                }
              />

              <MetricPill
                label="Alarma"
                value={
                  result.expectedMin != null && result.alarmMin != null
                    ? `< ${formatNumber(result.expectedMin)} o ≥ ${formatNumber(result.alarmMin)} °C`
                    : "—"
                }
              />
            </>
          )}

          {Array.isArray(result.metrics) &&
            result.metrics.map((m, i) => (
              <ConditionPill key={i} label={m.label} value={m.value} alarm={m.alarm} />
            ))}

          <MetricPill
            label="Carga"
            value={
              Array.isArray(result.loads)
                ? result.loads.length === 1 && result.compressorNumber != null
                  ? `C${result.compressorNumber}: ${result.loads[0]}%`
                  : result.loads.map((v, i) => `C${i + 1}: ${v}%`).join(" | ")
                : "—"
            }
          />
        </Stack>

        <Box sx={{ mb: 1 }}>
          <Stack direction="row" justifyContent="space-between" mb={0.6}>
            <Typography variant="caption" color="text.secondary">
              Nivel de riesgo
            </Typography>

            <Typography variant="caption" fontWeight={800} sx={{ color: ui.color }}>
              {Math.round(progress)}%
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 999,
              bgcolor: "#e5e7eb",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                backgroundColor: ui.color,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.65)",
            border: "1px solid rgba(226,232,240,0.9)",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {result.resultLabel || (result.status === "ok" ? "Resultado" : "Posible causa")}
          </Typography>

          <Typography variant="body2" fontWeight={700}>
            {result.cause || "—"}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AlgorithmsPanel({ results = [], headerExtra = null }) {
  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 3,
        background: "linear-gradient(135deg, #ffffff, #f8fafc)",
        boxShadow: "0 18px 45px rgba(15,23,42,0.07)",
        border: "1px solid #e5e7eb",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          mb={2.5}
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Diagnóstico inteligente
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Evaluación automática de condiciones de operación
            </Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
          >
            {headerExtra}

            <Chip
              label={`${results.length} algoritmo${results.length === 1 ? "" : "s"}`}
              sx={{
                bgcolor: "#0f172a",
                color: "#fff",
                fontWeight: 800,
                borderRadius: 3,
              }}
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(2, 1fr)",
              xl: "repeat(3, 1fr)",
            },
          }}
        >
          {results.map((result) => (
            <AlgorithmCard key={result.key} result={result} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}